import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'

import { buildLeadFormWaMessage } from 'src/components/LeadForm/buildLeadFormWaMessage'
import { useAuthProvider } from 'src/context/AuthProvider'
import { useClickIdsForPrefill } from 'src/hooks/useClickIdsForPrefill'
import { useGetPrice } from 'src/hooks/usePrice'
import { useWaSessionToken } from 'src/hooks/useWaSessionToken'
import { IConfiguration, ILocation, StorageTime } from 'src/types/graphql'
import { runAfterNextPaint } from 'src/utils/afterNextPaint'
import useAddToCart from 'src/utils/pixels/common'
import { isTraditionalVariant } from 'src/utils/pricing'
import { aliasWaSession } from 'src/utils/waSessionToken'

const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

type IProps = {
  location: ILocation
  activeConfig: IConfiguration
  storageTime?: StorageTime | ''
  quantity?: number
}

function WrapperWA({ location, activeConfig, storageTime, quantity }: IProps) {
  const { t } = useTranslation('details')
  const { getIsBrokerMode } = useGetPrice()
  const { currentUser } = useAuthProvider()
  const isBrokerMode = getIsBrokerMode(activeConfig)
  const isTraditionalMode = isTraditionalVariant(activeConfig)
  const handleAddToCart = useAddToCart()
  // Mount-gated (must be above the early return below) so the FAB's WA href
  // doesn't drift between SSR and client HTML — see useClickIdsForPrefill.
  const waClickIds = useClickIdsForPrefill()
  // Same mount-gating rationale, plus it waits for PostHog to actually be
  // live — a render-time `sessionId()` read is always `''` this early. Also
  // above the early return so hook order stays stable.
  const waSessionToken = useWaSessionToken()

  // 2026-05-26 (docs/plans/2026-05-26-001): hide the floating WhatsApp
  // FAB on Broker + Traditional listings. The PriceCard now renders a
  // green WhatsApp pill as the primary CTA on both surfaces (desktop
  // right rail + mobile sticky bar), so a second WA-green circle floating
  // over the page is a duplicate call — same destination, same prefill,
  // same handler. Monthly + other surfaces keep the FAB (their PriceCard
  // CTA is "Reservar" → checkout, so the FAB still serves a distinct
  // purpose). Fleet pill on Monthly+Parking is a separate decision tree
  // and stays untouched (the fleet trigger lives on the PriceCard itself,
  // not as a floating FAB).
  if (isBrokerMode || isTraditionalMode) return null

  // Defer to the canonical prefill builder. This gives us:
  //   • tracking-param stripping (no `utm_source=...&gclid=...` tail
  //     wrapping the URL onto 3-4 lines on mobile, which encouraged the
  //     accidental keymashing we saw in production around 2026-05-22)
  //   • single-`\n` line separators (iOS WhatsApp's draft input
  //     collapses `%0A%0A` to a single space on focus on some versions
  //     — see `buildLeadFormWaMessage` JSDoc)
  //   • session-token attribution via `?sid={token}` on the URL only,
  //     keeping the prefill clean of customer-visible tracking markers
  const userName =
    currentUser?.firstname ?? currentUser?.userChanges?.firstname ?? undefined
  const msg = buildLeadFormWaMessage({
    location,
    configuration: activeConfig,
    values: {
      name: userName ?? undefined,
      when: storageTime || undefined,
      quantity
    },
    greeting: 'Hola, me interesa este espacio:',
    sessionToken: waSessionToken,
    clickIds: waClickIds
  })

  // Monthly sticky bar is ~120-150px tall (chips row + price + scarcity +
  // minMonths) so the FAB floats above it at bottom-36 (144px). Admin FAB
  // on the left mirrors the same offset so both flank the screen at the
  // same height.
  const mobileBottom = 'bottom-36'

  return (
    <WhatsAppButton
      mobileBottom={mobileBottom}
      mobileVisible
      revealAt={0}
      msg={msg}
      label={t('BROKER_MODAL.CONSULT')}
      onClick={() => {
        // Merge the WA identity into this browser person before handing off,
        // so the server's `qualified_whatsapp_lead` doesn't land on a second
        // person and double-count the lead. On click (not mount) — this FAB
        // renders on every Monthly listing, and aliasing is an identifying
        // call under `person_profiles: 'identified_only'`.
        aliasWaSession(waSessionToken)
        // Fanout after the tap's paint (Jules, 2026-10-28); WhatsApp opening
        // hides the page, which flushes it — see runAfterNextPaint.
        runAfterNextPaint(() =>
          handleAddToCart('contact_wa', location, location.defaultConfiguration)
        )
      }}
    />
  )
}

export default WrapperWA
